/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import EventTags from 'apmui/EventTags';
import App from 'apmui/App';
import {
    IOktaMfaModel,
    IOktaMfaEvent,
    FORM_ID,
    NAV_FORM_ID,
    FIELD_TOTP,
    FIELD_PUSH,
    FIELD_OKTA,
    FIELD_YUBICO,
    FIELD_SMS,
    FIELD_EMAIL,
    Action,
} from './Model';
import IFormConfiguration from 'apmui/components/form/Model';
import 'apmui/components/form/Controller';
import { request, IRequestOptions } from 'apmui/util/request';
import { findField } from 'apmui/common/modelAPI';
import GetPRPQuery from 'apmui/util/GetPRPQuery';

const FACTOR_ROOT = '/okta_mfa/factors';

const TOTP_FACTOR = 'okta_totp';
const PUSH_FACTOR = 'okta_push';
const YUBICO_FACTOR = 'yubico_otp';

const app = App.get();

class Client {
    private finished: boolean;
    private query: string;

    constructor() {
        this.finished = false;
        const stateParam = GetPRPQuery();
        this.query = stateParam ? '?' + stateParam.split('=')[1] : '';
    }

    private sendRequest(url: string, options: Partial<IRequestOptions>, onSuccess: (obj: any) => void) {
        request(getHtconnFwdURL(url + this.query), options)
            .then((json) => {
                let data = null;
                if (json.response) {
                    data = JSON.parse(json.response);
                }
                onSuccess(data);
            })
            .catch((err) => {
                if (err.code == 409) {
                    return;
                }
                this.done();
            });
    }

    post(url: string, body: string, onSuccess: (obj: any) => void) {
        this.sendRequest(
            url,
            {
                method: 'POST',
                accept: 'application/json',
                contentType: 'text/plain',
                body: body,
            },
            onSuccess
        );
    }

    get(url: string, onSuccess: (obj: any) => void) {
        this.sendRequest(
            url,
            {
                accept: 'application/json',
            },
            onSuccess
        );
    }

    done() {
        if (this.finished) {
            return;
        }
        const form = document.forms.namedItem(NAV_FORM_ID);
        this.finished = true;
        (form as HTMLFormElement).submit();
    }
}

const client = new Client();

function getEnrollPath(factor: string) {
    const path = FACTOR_ROOT + '/' + factor + '/enroll';
    return path;
}

function getVerifyPath(factor: string) {
    const path = FACTOR_ROOT + '/' + factor + '/verify';
    return path;
}

function getActivatePath(factor: string) {
    const path = FACTOR_ROOT + '/' + factor + '/activate';
    return path;
}

function getHtconnFwdURL(queryString: string) {
    const url = '/vdesk/htconnector-fwd.php?u=' + queryString;
    return url;
}

function getFactorName(factor: string) {
    let val = '';
    switch (factor) {
        case PUSH_FACTOR:
        case TOTP_FACTOR: {
            val = FIELD_OKTA;
            break;
        }
        case YUBICO_FACTOR: {
            val = FIELD_YUBICO;
            break;
        }
    }
    return val;
}

function validateFormInputs(store: IOktaMfaModel, field: string) {
    const { strings, form } = store.oktaMfa;
    if (form) {
        switch (field) {
            case FIELD_TOTP: {
                const totpField = findField(form, FIELD_TOTP);
                if (totpField && (!totpField.value || !totpField.value.toString().match(/^\d+$/))) {
                    totpField.error = strings.codeError;
                    return false;
                }
                break;
            }
            case FIELD_YUBICO: {
                const yubicoField = findField(form, FIELD_YUBICO);
                if (yubicoField && !yubicoField.value) {
                    yubicoField.error = strings.codeError;
                    return false;
                }
                break;
            }
        }
    }
    return true;
}

function validatePhoneNumber(store: IOktaMfaModel) {
    const { strings, form, state } = store.oktaMfa;
    const numberText = findField(form, FIELD_SMS);
    if (numberText) {
        const number = numberText.value;
        if (number) {
            const trimNumber = number.toString().trim();
            if (trimNumber == '') {
                numberText.error = strings.phoneError;
                return false;
            }
            const reg = /^[0-9()+-]*$/;
            if (!reg.test(trimNumber)) {
                numberText.error = strings.phoneError;
                return false;
            }
        } else {
            numberText.error = strings.phoneError;
            return false;
        }
    }
    state.isMoreOption = false;
    return true;
}

function updateFactorState(store: IOktaMfaModel, factorListObj: []) {
    const { state } = store.oktaMfa;
    factorListObj.forEach(function(factor: string) {
        if (!state.currentFactor) {
            state.currentFactor = getFactorName(factor);
        }
        switch (factor) {
            case TOTP_FACTOR: {
                state.isOtp = true;
                break;
            }
            case PUSH_FACTOR: {
                state.isPush = true;
                break;
            }
            case YUBICO_FACTOR: {
                state.isYubico = true;
                break;
            }
        }
    });
}

function listReceived(factorListObj: any, store: IOktaMfaModel) {
    const { state } = store.oktaMfa;

    if (factorListObj.length === 0) {
        client.done();
    }

    let factors = factorListObj.filter(function(item: any) {
        return item.active;
    });

    state.factorsForEnrollment = factorListObj
        .filter((item: any) => !item.active)
        .map(function(item: any) {
            return item.name;
        });

    if (factors.length == 0) {
        state.isEnrollment = true;
        factors = factorListObj;
    }

    updateFactorState(
        store,
        factors.map(function(item: any) {
            return item.name;
        })
    );

    state.renderForm = true;
    renderUI();
}

function renderUI() {
    app.dispatch(EventTags.UPDATE);
}

function oktaEnroll(eventData: IOktaMfaEvent, store: IOktaMfaModel) {
    const { state } = store.oktaMfa;
    state.hasEnrollInfo = false;
    if (eventData.name == FIELD_TOTP) {
        client.post(getEnrollPath(TOTP_FACTOR), '', (json) => {
            state.enrollInfo = json;
            state.hasEnrollInfo = true;
            renderUI();
        });
    } else if (eventData.name == FIELD_PUSH) {
        client.post(getEnrollPath(PUSH_FACTOR), '', (json) => {
            state.pushEnrollInfo = json;
            state.hasEnrollInfo = true;
            oktaPushPoll(store);
            renderUI();
        });
    }
}

function oktaVerify(factor: string, store: IOktaMfaModel) {
    const { form, state } = store.oktaMfa;
    if (factor == FIELD_TOTP) {
        if (validateFormInputs(store, FIELD_TOTP)) {
            let url = getVerifyPath(TOTP_FACTOR);
            if (state.isEnrollment) {
                url = getActivatePath(TOTP_FACTOR);
            }

            const totpField = findField(form, FIELD_TOTP);
            let totp = '';
            if (totpField && totpField.value) {
                totp = totpField.value.toString();
            }
            client.post(url, totp, (json) => {
                if (state.isEnrollment) {
                    updateEnrollFactorList(store, [TOTP_FACTOR]);
                } else {
                    onResponse(store);
                }
            });
        }
    } else if (factor == FIELD_PUSH) {
        client.post(getVerifyPath(PUSH_FACTOR), '', (json) => {
            onResponse(store);
        });
    }
}

function oktaSendPushEnrollStatus(type: string, store: IOktaMfaModel) {
    const { state, form } = store.oktaMfa;
    let link;
    let number = '';
    if (type == FIELD_SMS) {
        if (!validatePhoneNumber(store)) {
            return;
        }
        link = state.pushEnrollInfo.smsLink;
        const phoneField = findField(form, FIELD_SMS);
        if (phoneField && phoneField.value) {
            number = phoneField.value.toString();
            state.phoneNumber = number;
        }
    } else {
        link = state.pushEnrollInfo.emailLink;
    }
    client.post(link, number, (json) => {
        state.linkSent = true;
        renderUI();
    });
}

function oktaPushPoll(store: IOktaMfaModel) {
    client.post(getActivatePath(PUSH_FACTOR), '', (json) => {
        updateEnrollFactorList(store, [TOTP_FACTOR, PUSH_FACTOR]);
    });
}

function handleYubico(store: IOktaMfaModel) {
    if (!validateFormInputs(store, FIELD_YUBICO)) {
        return;
    }
    const { state, form } = store.oktaMfa;
    let url;
    let code = '';
    if (state.isEnrollment) {
        url = getEnrollPath(YUBICO_FACTOR);
    } else {
        url = getVerifyPath(YUBICO_FACTOR);
    }
    const passCode = findField(form, FIELD_YUBICO);
    if (passCode && passCode.value) {
        code = passCode.value.toString();
    }
    client.post(url, code, (json) => {
        if (state.isEnrollment) {
            updateEnrollFactorList(store, [YUBICO_FACTOR]);
        } else {
            onResponse(store);
        }
    });
}

function updateEnrollFactorList(store: IOktaMfaModel, factors: Array<string>) {
    const { state } = store.oktaMfa;
    state.factorsForEnrollment = state.factorsForEnrollment.filter((factor) => factors.indexOf(factor) === -1);
    state.resetEnrollmentPage = true;
    onResponse(store);
}

function onResponse(store: IOktaMfaModel) {
    const { state } = store.oktaMfa;
    if (state.doEnrollAfterVerify) {
        beginEnrollment(store);
        renderUI();
    } else {
        if (canEnroll(store)) {
            renderUI();
            return;
        }
        client.done();
    }
}

function canEnroll(store: IOktaMfaModel) {
    const { state } = store.oktaMfa;
    if (state.isEnrollment && state.factorsForEnrollment.length > 0) {
        return true;
    }
    return false;
}

function beginEnrollment(store: IOktaMfaModel) {
    const { state } = store.oktaMfa;
    reset(store);
    init(store);
    state.isEnrollment = true;
    updateFactorState(store, state.factorsForEnrollment as any);
}

function init(store: IOktaMfaModel) {
    const page = store[store.pageType];
    page.state.hasEnrollInfo = false;
    page.state.showEnrollPageBtn = false;
    page.state.linkSent = false;
    page.state.isMoreOption = true;
    const form: IFormConfiguration = {
        id: FORM_ID,
        title: '',
        submitCaption: '',
        method: 'POST',
        fields: [],
    };
    page.form = form;
}

function reset(store: IOktaMfaModel) {
    const page = store[store.pageType];
    page.state.isPush = false;
    page.state.isOtp = false;
    page.state.isYubico = false;
    page.state.isEnrollment = false;
    page.state.currentFactor = '';
    page.state.doEnrollAfterVerify = false;
}

app.subscribeDefault(EventTags.CONFIGURE, (event, store) => {
    if (store.pageType !== 'oktaMfa') {
        return;
    }
    const page = store[store.pageType];
    page.state.renderForm = false;
    page.state.resetEnrollmentPage = false;
    page.state.factorsForEnrollment = [];
    reset(store);
    init(store);
});

app.subscribeDefault(EventTags.START, (event, store) => {
    if (store.pageType !== 'oktaMfa') {
        return;
    }
    client.get('/okta_mfa/factors', (json) => {
        listReceived(json, store);
    });
});

app.subscribeDefault(EventTags.FORM_SUBMIT, (event, store) => {
    if (store.pageType !== 'oktaMfa') {
        return;
    }
    event.preventDefault();
    const { form } = store.oktaMfa;
    const numberText = findField(form, FIELD_SMS);
    if (numberText) {
        oktaSendPushEnrollStatus(FIELD_SMS, store);
        return;
    }
    const totpField = findField(form, FIELD_TOTP);
    if (totpField) {
        oktaVerify(FIELD_TOTP, store);
        return;
    }
    const yubicoField = findField(form, FIELD_YUBICO);
    if (yubicoField) {
        handleYubico(store);
        return;
    }
});

app.subscribeDefault(EventTags.OKTA_MFA_EVENT, ({ detail: eventData }, store) => {
    if (store.pageType !== 'oktaMfa') {
        return;
    }
    if (eventData.action == Action.DONE) {
        client.done();
    }
    if (eventData.action == Action.INIT) {
        init(store);
        return;
    }
    if (eventData.action == Action.VERIFY) {
        oktaVerify(eventData.name as string, store);
    } else if (eventData.action == Action.UPDATE_STATE) {
        store.oktaMfa.state[eventData.name as string] = eventData.value;
    } else if (eventData.action == Action.ENROLL && (eventData.name == FIELD_SMS || eventData.name == FIELD_EMAIL)) {
        oktaSendPushEnrollStatus(eventData.name, store);
    } else {
        oktaEnroll(eventData, store);
    }
});
